import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, type FaqEntry } from "../api";
import { Markdown } from "./Markdown";

interface Props {
  slug: string;
  /** Bump to reload (e.g. after an answer was saved from the chat). */
  refreshKey: number;
  fullPage?: boolean;
}

export function FaqPanel({ slug, refreshKey, fullPage = false }: Props) {
  const [entries, setEntries] = useState<FaqEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api
      .faq(slug)
      .then(setEntries)
      .catch((e) => setError(e.message));
  }, [slug, refreshKey]);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!entries || !q) return entries;
    return entries.filter((e) => e.question.toLowerCase().includes(q) || e.answer.toLowerCase().includes(q));
  }, [entries, filter]);

  function replace(updated: FaqEntry) {
    setEntries((prev) => prev?.map((e) => (e.id === updated.id ? updated : e)) ?? null);
  }

  function remove(id: number) {
    setEntries((prev) => prev?.filter((e) => e.id !== id) ?? null);
  }

  return (
    <section className={fullPage ? "faq faq-page" : "faq"} aria-label="My FAQ">
      <div className="faq-header">
        <div>
          <h2>My FAQ</h2>
          <p className="muted small">Private to you. Save answers from the chat to build it up.</p>
        </div>
        {!fullPage && (
          <Link className="btn btn-sm btn-ghost" to={`/courses/${slug}/faq`} title="Open the FAQ as a full page">
            Full page ↗
          </Link>
        )}
      </div>
      {entries && entries.length > 0 && (
        <input
          className="faq-filter"
          type="search"
          placeholder="Search your FAQ…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          aria-label="Search FAQ"
        />
      )}
      {error && <p className="form-error">{error}</p>}
      {!entries && !error && <p className="muted">Loading…</p>}
      {entries && entries.length === 0 && (
        <div className="faq-empty">
          <p>No saved questions yet.</p>
          <p className="muted small">
            Ask something in the chat, then click <strong>Save to FAQ</strong> under an answer you want to keep.
          </p>
        </div>
      )}
      {visible && entries && entries.length > 0 && visible.length === 0 && (
        <p className="muted">Nothing matches “{filter}”.</p>
      )}
      <ul className="faq-list">
        {visible?.map((entry) => (
          <FaqItem
            key={entry.id}
            entry={entry}
            defaultOpen={fullPage}
            onUpdated={replace}
            onDeleted={() => remove(entry.id)}
            onError={setError}
          />
        ))}
      </ul>
    </section>
  );
}

function FaqItem({
  entry,
  defaultOpen,
  onUpdated,
  onDeleted,
  onError,
}: {
  entry: FaqEntry;
  defaultOpen: boolean;
  onUpdated: (e: FaqEntry) => void;
  onDeleted: () => void;
  onError: (msg: string) => void;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [editing, setEditing] = useState(false);
  const [question, setQuestion] = useState(entry.question);
  const [answer, setAnswer] = useState(entry.answer);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      onUpdated(await api.updateFaq(entry.id, { question: question.trim(), answer: answer.trim() }));
      setEditing(false);
    } catch (e) {
      onError(e instanceof Error ? e.message : "Could not save");
    } finally {
      setBusy(false);
    }
  }

  async function del() {
    setBusy(true);
    try {
      await api.deleteFaq(entry.id);
      onDeleted();
    } catch (e) {
      onError(e instanceof Error ? e.message : "Could not delete");
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <li className="faq-item editing">
        <label>
          Question
          <input value={question} onChange={(e) => setQuestion(e.target.value)} maxLength={8000} />
        </label>
        <label>
          Answer (Markdown)
          <textarea value={answer} onChange={(e) => setAnswer(e.target.value)} rows={8} />
        </label>
        <div className="faq-actions">
          <button className="btn btn-primary btn-sm" onClick={() => void save()} disabled={busy || !question.trim() || !answer.trim()}>
            Save
          </button>
          <button
            className="btn btn-sm"
            onClick={() => {
              setQuestion(entry.question);
              setAnswer(entry.answer);
              setEditing(false);
            }}
          >
            Cancel
          </button>
        </div>
      </li>
    );
  }

  return (
    <li className={open ? "faq-item open" : "faq-item"}>
      <button className="faq-question" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className="faq-chevron" aria-hidden>
          {open ? "▾" : "▸"}
        </span>
        {entry.question}
      </button>
      {open && (
        <div className="faq-answer">
          <Markdown text={entry.answer} />
          <div className="faq-actions">
            <span className="muted small">Saved {new Date(entry.created_at).toLocaleDateString()}</span>
            <span className="spacer" />
            <button className="link-btn" onClick={() => setEditing(true)}>
              Edit
            </button>
            {confirmDelete ? (
              <button className="link-btn danger" onClick={() => void del()} disabled={busy}>
                Confirm delete
              </button>
            ) : (
              <button className="link-btn" onClick={() => setConfirmDelete(true)}>
                Delete
              </button>
            )}
          </div>
        </div>
      )}
    </li>
  );
}
